import { inject, injectable } from 'inversify'
import { merge } from 'lodash-es'
import { ofetch } from 'ofetch'
import type { $fetch, FetchOptions, FetchRequest } from 'ofetch'

import type { ApiClient, ApiClientResponse } from './types'
import { LinkCreator } from '../LinkCreator'

@injectable()
export class HTTPClient implements ApiClient {
  fetch: typeof $fetch
  fetchOptions: FetchOptions

  constructor(
    @inject(LinkCreator)
    readonly linkCreator: LinkCreator,
  ) {
    this.fetchOptions = {
      baseURL: this.linkCreator.getBase(),
      retry: 0,
    }
    this.fetch = ofetch.create(this.fetchOptions)
  }

  setBaseUrl(baseUrl: string): void {
    this.fetchOptions.baseURL = baseUrl
    this.fetch = ofetch.create(this.fetchOptions)
  }

  get<T = unknown>(
    url: string,
    options?: Omit<FetchOptions<'json' | 'stream'>, 'body'>,
  ): ApiClientResponse<T> {
    return this.send(url, {
      ...this.getOptionsWith(options),
      method: 'GET',
    })
  }

  post<T = unknown>(
    url: string,
    body?: RequestInit['body'] | Record<string, unknown>,
    options?: FetchOptions<'json' | 'stream'>,
  ): ApiClientResponse<T> {
    return this.send(url, {
      ...this.getOptionsWith(options),
      method: 'POST',
      body,
    })
  }

  put<T = unknown>(
    url: string,
    body?: RequestInit['body'] | Record<string, unknown>,
    options?: FetchOptions<'json' | 'stream'>,
  ): ApiClientResponse<T> {
    return this.send(url, {
      ...this.getOptionsWith(options),
      method: 'PUT',
      body,
    })
  }

  patch<T = unknown>(
    url: string,
    body?: RequestInit['body'] | Record<string, unknown>,
    options?: FetchOptions<'json' | 'stream'>,
  ): ApiClientResponse<T> {
    return this.send(url, {
      ...this.getOptionsWith(options),
      method: 'PATCH',
      body,
    })
  }

  delete<T = unknown>(
    url: string,
    options?: FetchOptions<'json' | 'stream'>,
  ): ApiClientResponse<T> {
    return this.send(url, {
      ...this.getOptionsWith(options),
      method: 'DELETE',
    })
  }

  $post<T = unknown>(
    url: string,
    body?: RequestInit['body'] | Record<string, unknown>,
    options?: FetchOptions<'json' | 'stream'>,
  ): ApiClientResponse<T> {
    const parseOptions = { responseType: 'json' }
    return this.post(url, body, merge({}, options || {}, parseOptions))
  }

  $get<T = unknown>(
    url: string,
    options?: Omit<FetchOptions<'json' | 'stream'>, 'body'>,
  ): ApiClientResponse<T> {
    const parseOptions = { responseType: 'json' }
    return this.get(url, merge({}, options || {}, parseOptions))
  }

  $patch<T = unknown>(
    url: string,
    body?: RequestInit['body'] | Record<string, unknown>,
    options?: FetchOptions<'json' | 'stream'>,
  ): ApiClientResponse<T> {
    const parseOptions = { responseType: 'json' }
    return this.patch(url, body, merge({}, options || {}, parseOptions))
  }

  private send<T = unknown>(
    request: FetchRequest,
    options?: FetchOptions<'json' | 'stream'>,
  ): ApiClientResponse<T> {
    const abortController = new AbortController()

    return {
      response: this.fetch.raw<T>(request, {
        ...options,
        signal: abortController.signal,
      } as any),
      abort: () => abortController.abort(),
    }
  }

  private getOptionsWith(options?: FetchOptions<'json' | 'stream'>) {
    return merge({}, this.fetchOptions, options)
  }
}
